import React from 'react';
import { CheckCircle2, RotateCcw, ArrowRight } from 'lucide-react';
import { KAccuracyResults } from './types';
import { motion } from 'framer-motion';

interface ResultScreenProps {
  results: KAccuracyResults;
}

const ResultScreen: React.FC<ResultScreenProps> = ({ results }) => {
  const handleContinue = () => {
    if (results.passed) {
      window.location.replace('/');
    } else {
      window.location.reload();
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-6">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Pass/Fail Header */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <h1 className="text-4xl font-bold mb-4">
            <span className={`${results.passed ? 'text-green-600' : 'text-red-600'}`}>
              {results.passed ? 'Passed!' : 'Not Yet'}
            </span>
          </h1>
          {results.passed ? (
            <p className="text-xl text-gray-600">
              Great work! You're ready for Addition Within 10.
            </p>
          ) : (
            <p className="text-xl text-gray-600">
              Keep practicing to improve your accuracy.
            </p>
          )}
        </motion.div>

        {/* Results Cards */}
        <div className="flex justify-center">
          {/* Score Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-md p-6 w-full max-w-sm"
          >
            <div className="flex flex-col">
              <span className="text-gray-500 text-base mb-1">Your Score</span>
              <div className="flex justify-between items-center">
                <span className={`text-[2.5rem] font-bold ${results.passed ? 'text-green-600' : 'text-red-600'}`}>
                  {results.correctAnswers}/20
                </span>
                <CheckCircle2 className={`w-6 h-6 ${results.passed ? 'text-green-500' : 'text-red-500'}`} />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Action Buttons */}
        {results.passed ? (
          <div className="flex justify-center">
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleContinue}
              className="w-full max-w-md px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500
                text-white rounded-lg font-bold text-base shadow-md hover:shadow-lg
                transition-all duration-300 flex items-center justify-center gap-2"
            >
              <ArrowRight className="w-5 h-5" />
              <span>Continue</span>
            </motion.button>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleRetry}
              className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500
                text-white rounded-lg font-bold text-base shadow-md hover:shadow-lg
                transition-all duration-300 flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-5 h-5" />
              <span>Retry Assessment</span>
            </motion.button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultScreen;
